import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, Pencil, Trash2, Wheat } from "lucide-react";
import type { RawMaterial } from "@shared/schema";

const rawMaterialFormSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  articleNumber: z.string().optional(),
  packaging: z.string().min(1, "Förpackning krävs"),
  storageLocation: z.string().optional(),
  price: z.string().min(1, "Pris krävs"),
});

type RawMaterialFormData = z.infer<typeof rawMaterialFormSchema>;

export default function AdminRawMaterials() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RawMaterial | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: materials, isLoading } = useQuery<RawMaterial[]>({
    queryKey: ["/api/admin/raw-materials"],
  });

  const form = useForm<RawMaterialFormData>({
    resolver: zodResolver(rawMaterialFormSchema),
    defaultValues: {
      name: "",
      articleNumber: "",
      packaging: "Säck",
      storageLocation: "",
      price: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: RawMaterialFormData) =>
      apiRequest("POST", "/api/admin/raw-materials", data),
    onSuccess: () => {
      toast({ title: "Råvara skapad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/raw-materials"] });
      setDialogOpen(false);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skapa råvara.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: RawMaterialFormData & { id: string }) =>
      apiRequest("PATCH", `/api/admin/raw-materials/${data.id}`, data),
    onSuccess: () => {
      toast({ title: "Råvara uppdaterad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/raw-materials"] });
      setDialogOpen(false);
      setEditingItem(null);
      form.reset();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera råvara.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/raw-materials/${id}`),
    onSuccess: () => {
      toast({ title: "Råvara raderad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/raw-materials"] });
      setDeletingId(null);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte radera råvara.", variant: "destructive" });
    },
  });

  const openCreate = () => {
    setEditingItem(null);
    form.reset({ name: "", articleNumber: "", packaging: "Säck", storageLocation: "", price: "" });
    setDialogOpen(true);
  };

  const openEdit = (item: RawMaterial) => {
    setEditingItem(item);
    form.reset({
      name: item.name,
      articleNumber: item.articleNumber || "",
      packaging: item.packaging,
      storageLocation: item.storageLocation || "",
      price: item.price,
    });
    setDialogOpen(true);
  };

  const onSubmit = (data: RawMaterialFormData) => {
    if (editingItem) {
      updateMutation.mutate({ ...data, id: editingItem.id });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Råvarulista</h1>
        <p className="text-muted-foreground">Hantera råvaror och ingredienser</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Råvaror</CardTitle>
            <CardDescription>Alla registrerade råvaror</CardDescription>
          </div>
          <Button onClick={openCreate} data-testid="button-add-raw-material">
            <Plus className="h-4 w-4 mr-2" />
            Ny råvara
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : materials && materials.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>Art.nr</TableHead>
                    <TableHead>Förpackning</TableHead>
                    <TableHead>Lagerplats</TableHead>
                    <TableHead>Pris (kr/kg)</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materials.map((mat) => (
                    <TableRow key={mat.id}>
                      <TableCell className="font-medium">{mat.name}</TableCell>
                      <TableCell>{mat.articleNumber || "-"}</TableCell>
                      <TableCell>{mat.packaging}</TableCell>
                      <TableCell>{mat.storageLocation || "-"}</TableCell>
                      <TableCell>{mat.price} kr</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(mat)} data-testid={`button-edit-material-${mat.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog open={deletingId === mat.id} onOpenChange={(open) => !open && setDeletingId(null)}>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeletingId(mat.id)} data-testid={`button-delete-material-${mat.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Radera råvara?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Detta kommer att permanent radera {mat.name}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteMutation.mutate(mat.id)} className="bg-destructive text-destructive-foreground">
                                  Radera
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Wheat className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga råvaror registrerade</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Redigera råvara" : "Ny råvara"}</DialogTitle>
            <DialogDescription>
              {editingItem ? "Uppdatera råvaruinformation" : "Lägg till en ny råvara"}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Namn</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="input-material-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="articleNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Artikelnummer</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-material-article-number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="packaging"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Förpackning</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="input-material-packaging">
                            <SelectValue placeholder="Välj förpackning" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Säck">Säck</SelectItem>
                          <SelectItem value="Kartong">Kartong</SelectItem>
                          <SelectItem value="Hink">Hink</SelectItem>
                          <SelectItem value="Påse">Påse</SelectItem>
                          <SelectItem value="Flaska">Flaska</SelectItem>
                          <SelectItem value="Dunk">Dunk</SelectItem>
                          <SelectItem value="Burk">Burk</SelectItem>
                          <SelectItem value="Låda">Låda</SelectItem>
                          <SelectItem value="St">St</SelectItem>
                          <SelectItem value="Kg">Kg</SelectItem>
                          <SelectItem value="Liter">Liter</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="storageLocation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lagerplats</FormLabel>
                      <FormControl>
                        <Input {...field} data-testid="input-material-storage" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pris (kr/kg)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step="0.01" data-testid="input-material-price" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-save-material">
                  {(createMutation.isPending || updateMutation.isPending) ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
